import { useMemo, useState } from "preact/hooks"
import { Bar, BarChart, CartesianGrid, Cell, LineChart, Line, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { BASELINE_COLOR, DataTable, GRID_COLOR, Legend, MUTED_COLOR, Panel, WORKING_COLOR, column, row_tooltip } from "../trade_review/trade_review_parts"
import { fmt_day, fmt_int, fmt_signed_pct, fmt_usd2, fmt_usd_short } from "../trade_review/trade_review_model"
import { curve_row, is_measurable, kehe_event, model_row, model_slices, promo_lift_review, promo_span, search_kehe_events, weekly_chart, weekly_promo, weekly_retailers } from "./promo_lift_review_model"
import { ReadPill } from "./promo_lift_review_parts"

const LINE_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#8a5cd1", "#c9a227", "#d64f8c"]

function ModelRanges({ rows }: { rows: model_row[] }) {
  const low = Math.min(-0.2, ...rows.map((row: model_row) => row.lo)) - 0.02
  const high = Math.max(0.6, ...rows.map((row: model_row) => row.hi)) + 0.02

  function left(value: number): string {
    return `${((value - low) / (high - low)) * 100}%`
  }

  return (
    <Panel title="Lift by depth, line and tactic" note="Pooled model over every KeHE promo week, January to October 2026. Dot is the lift, bar its 95% range. A bar that crosses zero is not a measurable lift.">
      <div class="space-y-4">
        {model_slices(rows).map((group: { slice: string; rows: model_row[] }) => (
          <div key={group.slice} class="space-y-1.5">
            <h3 class="text-sm font-medium">{group.slice}</h3>
            {group.rows.map((row: model_row) => {
              const color = is_measurable(row) ? WORKING_COLOR : MUTED_COLOR
              return (
                <div key={row.term} class="flex items-center gap-3 text-sm" title={`${fmt_signed_pct(row.lift)}, range ${fmt_signed_pct(row.lo)} to ${fmt_signed_pct(row.hi)}, ${fmt_int(row.weeks)} promo item-weeks`}>
                  <div class="w-48 shrink-0 truncate text-right text-muted-foreground">{row.term}</div>
                  <div class="relative h-5 flex-1 min-w-0">
                    <div class="absolute inset-y-0 w-px" style={{ left: left(0), background: BASELINE_COLOR }} />
                    <div class="absolute top-1/2 h-1 -translate-y-1/2 rounded opacity-50" style={{ left: left(row.lo), width: `calc(${left(row.hi)} - ${left(row.lo)})`, background: color }} />
                    <div class="absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ left: left(row.lift), background: color }} />
                  </div>
                  <div class="w-12 shrink-0 text-right tabular-nums">{fmt_signed_pct(row.lift)}</div>
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </Panel>
  )
}

function curve_label(week: number): string {
  if (week === 0) return "First promo week"
  return week < 0 ? `${-week} weeks before` : `${week} weeks after start`
}

function PromoShape({ rows }: { rows: curve_row[] }) {
  return (
    <Panel title="What a promo looks like in shipments" note="Median week against the promo's own baseline (1.0), lined up on the first promo week. Blue bars are promo weeks.">
      <div class="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={GRID_COLOR} />
            <XAxis dataKey="week" tickFormatter={(week: number) => (week > 0 ? `+${week}` : String(week))} tick={{ fontSize: 12 }} />
            <YAxis width={36} tick={{ fontSize: 12 }} tickFormatter={(value: number) => value.toFixed(1)} />
            <ReferenceLine y={1} stroke={BASELINE_COLOR} strokeDasharray="4 3" />
            <Tooltip content={row_tooltip((row: curve_row) => [curve_label(row.week), `${row.index.toFixed(2)}× baseline`, `${fmt_int(row.events)} promos`]) as never} />
            <Bar dataKey="index" radius={[2, 2, 0, 0]} isAnimationActive={false}>
              {rows.map((row: curve_row) => <Cell key={row.week} fill={row.in_promo >= 0.5 ? WORKING_COLOR : MUTED_COLOR} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  )
}

function WeeklyShipments({ data }: { data: promo_lift_review }) {
  const retailers = useMemo(() => weekly_retailers(data.kehe_weekly), [data.kehe_weekly])
  const [retailer, set_retailer] = useState(retailers[0] ?? "")
  const chart = useMemo(() => weekly_chart(data.kehe_weekly, retailer), [data.kehe_weekly, retailer])
  const weeks = chart.points.map((point: Record<string, number | string>) => String(point.week))
  const promos = data.kehe_weekly_promos.filter((row: weekly_promo) => row.retailer === retailer)
  return (
    <Panel title="Weekly KeHE shipments with each promo laid on top" note="Units KeHE shipped to the retailer each week, by product line. Shaded bands are Promomash promo windows.">
      <div class="space-y-1">
        <Label>Retailer</Label>
        <Select value={retailer} onValueChange={set_retailer}>
          <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>{retailers.map((name: string) => <SelectItem key={name} value={name}>{name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div class="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chart.points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={GRID_COLOR} />
            <XAxis dataKey="week" tickFormatter={fmt_day} minTickGap={28} tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={fmt_int} width={52} tick={{ fontSize: 12 }} />
            {promos.map((row: weekly_promo) => {
              const span = promo_span(weeks, row.start, row.end)
              return span ? <ReferenceArea key={`${row.promo}${row.start}`} x1={span.first} x2={span.last} fill={WORKING_COLOR} fillOpacity={0.08} ifOverflow="extendDomain" /> : null
            })}
            <Tooltip
              content={row_tooltip((point: Record<string, number | string>) => [
                `Week of ${fmt_day(String(point.week))}`,
                ...chart.lines.map((line: string) => `${line}: ${fmt_int(Number(point[line] ?? 0))} units`),
                ...promos.filter((row: weekly_promo) => { const span = promo_span([String(point.week)], row.start, row.end); return span != null }).map((row: weekly_promo) => row.promo),
              ]) as never}
            />
            {chart.lines.map((line: string, index: number) => (
              <Line key={line} dataKey={line} stroke={LINE_COLORS[index % LINE_COLORS.length]} strokeWidth={2} dot={false} isAnimationActive={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <Legend items={[...chart.lines.map((line: string, index: number) => ({ label: line, color: LINE_COLORS[index % LINE_COLORS.length] })), { label: "Promo window", color: `${WORKING_COLOR}22` }]} />
    </Panel>
  )
}

const EVENT_COLUMNS: column<kehe_event>[] = [
  { header: "Promo", left: true, wrap: true, cell: (row: kehe_event) => row.promo },
  { header: "KeHE retailer", left: true, cell: (row: kehe_event) => row.retailer },
  { header: "Start", left: true, cell: (row: kehe_event) => fmt_day(row.start) },
  { header: "Weeks", cell: (row: kehe_event) => fmt_int(row.weeks) },
  { header: "Items", cell: (row: kehe_event) => fmt_int(row.items) },
  { header: "Scan/unit", cell: (row: kehe_event) => (row.scan == null ? "" : fmt_usd2(row.scan)) },
  { header: "Base/wk", cell: (row: kehe_event) => fmt_int(row.base_wk) },
  { header: "Lift", cell: (row: kehe_event) => fmt_signed_pct(row.lift) },
  { header: "Planned", cell: (row: kehe_event) => (row.plan == null ? "" : fmt_signed_pct(row.plan)) },
  { header: "Extra units", cell: (row: kehe_event) => fmt_int(row.extra_units) },
  { header: "Matched spend", cell: (row: kehe_event) => (row.matched == null ? "" : fmt_usd_short(row.matched)) },
  { header: "Planned spend", cell: (row: kehe_event) => (row.planned == null ? "" : fmt_usd_short(row.planned)) },
  { header: "Read", left: true, cell: (row: kehe_event) => <ReadPill read={row.read} /> },
]

export function KeheTab({ data }: { data: promo_lift_review }) {
  const [query, set_query] = useState("")
  return (
    <div class="space-y-6">
      <div class="grid gap-4 xl:grid-cols-2">
        <ModelRanges rows={data.kehe_model} />
        <PromoShape rows={data.kehe_curve} />
      </div>
      <WeeklyShipments data={data} />
      <Panel title="Every KeHE promo we measured" note="Lift is the event's extra units spread over its promo weeks. Too noisy means the baseline moved more than 40% from before to after, or varied too much week to week to read.">
        <Input className="max-w-xs" placeholder="Search promo or retailer" value={query} onInput={(event: Event) => set_query((event.target as HTMLInputElement).value)} />
        <DataTable tall columns={EVENT_COLUMNS} rows={search_kehe_events(data.kehe_events, query)} row_key={(row: kehe_event) => `${row.promo}|${row.start}`} />
      </Panel>
    </div>
  )
}
